import React, { useState } from "react";
import styles from "./FAQSection.module.scss";
import HeartSVG from "./HeartSVG";
import MOQSVG from "./MOQSVG";
import ComplianceSVG from "./ComplianceSVG";
import QualitySVG from "./QualitySVG";
import ContactUsFormModal from "../ContactUsFormModal/ContactUsFormModal";
import { getCDNImageUrl } from "@/utils/Common";
import { trackFaqClicked } from "@/utils/gtmEvents";

// Default FAQ list with SVGs for fallback
const defaultFaqList = [
  // {
  //   id: 1,
  //   question: "Can I request a custom blend or formulation?",
  //   answer:
  //     "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
  //   svg: HeartSVG,
  // },
  // {
  //   id: 2,
  //   question: "What is your minimum order quantity (MOQ)?",
  //   answer:
  //     "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
  //   svg: MOQSVG,
  // },
  // {
  //   id: 3,
  //   question: "Are your products REACH or FDA compliant?",
  //   answer:
  //     "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
  //   svg: ComplianceSVG,
  // },
  // {
  //   id: 4,
  //   question: "How do you ensure product quality and consistency?",
  //   answer:
  //     "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
  //   svg: QualitySVG,
  // },
];

const FaqItem = ({ faq, isExpanded, onToggle }) => {
  return (
    <div className={`${styles.faq} ${isExpanded ? styles.expanded : ""}`}>
      {/* FAQ Header - clickable on mobile */}
      <div className={styles.faqHeader} onClick={onToggle}>
        {/* Concentric SVG */}
        {/* <div className={styles.concentricSVG}>{faq.svg()}</div> */}
        {/* Question and Toggle */}
        <div className={styles.questionAndToggle}>
          {/* Question */}
          <div className={styles.question}>{faq.question}</div>
          {/* Toggle Arrow - only visible on mobile */}
          <div className={styles.toggleArrow}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={isExpanded ? styles.rotated : ""}
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
      {/* Answer - collapsible on mobile */}
      <div className={styles.answerContainer}>
        <div className={styles.answer}>{faq.answer}</div>
      </div>
    </div>
  );
};

export const ContactCard = ({
  additionalStyles,
  onOpen,
  showImages = true,
  title = "Still have questions?",
  description = "Can't find the answer you're looking for? Please chat to our friendly team.",
}) => {
  return (
    <div className={styles.contactCard} style={{ ...additionalStyles }}>
      {/* Profile Images */}
      {showImages && (
        <div className={styles.profileImages}>
          <div className={styles.profileImage1}>
            <img
              src={getCDNImageUrl("contact-dummy-img.webp")}
              alt="Profile 1"
            />
          </div>
          <div className={styles.profileImage2}>
            <img
              src={getCDNImageUrl("contact-dummy-img.webp")}
              alt="Profile 2"
            />
          </div>
          <div className={styles.profileImage3}>
            <img
              src={getCDNImageUrl("contact-dummy-img.webp")}
              alt="Profile 3"
            />
          </div>
        </div>
      )}
      {/* Title */}
      <h3>{title}</h3>
      {/* description */}
      <p>{description}</p>
      {/* Get in touch button */}
      <button className={styles.getInTouchButton} onClick={onOpen}>
        Get in touch
      </button>
    </div>
  );
};

function FAQSection({
  faqs = [],
  industryName = "",
  description = "",
  hideContactCard = false,
  backgroundColor,
  // Small uppercase coral label above the heading (Figma 4670-7146). OPT-IN:
  // omitted → nothing renders, so the 8 non-landing-page consumers (product
  // pages, /industry/*, /tail-spend-management, /food-and-beverages,
  // /elchemy-tech, /request-a-sample, RegulatoryCompliance, ProductFAQ) are
  // byte-identical to before. Only the landing pages pass it, via FaqBlock.
  eyebrow = "",
}) {
  const [expandedFAQ, setExpandedFAQ] = useState(1);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Use FAQs from props if available, otherwise use default list
  const faqList =
    faqs.length > 0
      ? faqs.map((faq, index) => ({
          id: index + 1,
          question: faq.question,
          answer: faq.answer,
          svg: defaultFaqList[index % defaultFaqList.length]?.svg || HeartSVG, // Cycle through default SVGs
        }))
      : defaultFaqList;

  const toggleFAQ = (faqId) => {
    const isExpanding = expandedFAQ !== faqId;
    setExpandedFAQ(expandedFAQ === faqId ? null : faqId);

    // Track FAQ_CLICKED event only when expanding
    if (isExpanding) {
      const faq = faqList.find((f) => f.id === faqId);
      if (faq) {
        const pageUrl = typeof window !== "undefined" ? window.location.href : "";
        trackFaqClicked(pageUrl, industryName, faq.question);
      }
    }
  };

  return (
    <div className={styles.faqSection} style={backgroundColor ? { backgroundColor } : undefined}>
      {/* Small Heading */}
      {/* <h6>FAQs</h6> */}
      {/* Opt-in coral eyebrow (Figma 4670-7146). Rendered only when a caller
          supplies it, so existing consumers are untouched. */}
      {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
      {/* Big Heading */}
      {/* The " - {name}" suffix is dropped when no name is supplied, otherwise
          the heading renders as a dangling "FAQs - ". Every other caller passes
          a real industryName, so their heading is unchanged. */}
      <h2>{industryName ? `FAQs - ${industryName}` : "FAQs"}</h2>
      {/* Description */}
      {description && <p>{description}</p>}
      {/* FAQ List and Contact Card */}
      <div className={styles.faqListAndContactCard}>
        {/* FAQ List */}
        <div className={styles.faqList}>
          {faqList.map((faq) => (
            <FaqItem
              key={faq.id}
              faq={faq}
              isExpanded={expandedFAQ === faq.id}
              onToggle={() => toggleFAQ(faq.id)}
            />
          ))}
        </div>
        {/* Contact Card */}
        {!hideContactCard && (
          <ContactCard
            description="Our team is here to provide clarity or address any questions you may have"
            onOpen={() => setIsContactModalOpen(true)}
          />
        )}
      </div>
      {/* Contact Us Modal */}
      <ContactUsFormModal
        open={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </div>
  );
}

export default FAQSection;
